import { useState, useEffect } from 'react';
import {
  validatePhone,
  validateAgreement,
  validateMortgageAmount,
  validateMortgageRate,
  createValidationError,
  isPhoneError,
  isAgreementError,
} from '../utils';

import { type ILead, ValidationErrorType, type ValidationError, IRefinLeadBannerProps } from '../types';
import { useToast } from '@cian/ui-kit';
import { useTracking } from '../../../utils/tracking/useTracking';

export const useRefinLeadBanner = ({
  sendLead,
  isLeadSending,
  isLeadSuccessfullySent,
  sendRefinLead,
  isRefinLeadSending,
  isRefinLeadSuccessfullySent,
  isLeadError,
  isRefinLeadError,
}: IRefinLeadBannerProps) => {
  const [error, setError] = useState<ValidationError>(null);
  const [lead, setLead] = useState<ILead>({
    phone: '',
    hasMortgage: true,
    mortgageAmountRemaining: undefined,
    mortgageRate: undefined,
    agreeToGetAds: false,
  });

  const apiToast = useToast({
    placement: 'top-right',
  });

  const { trackInputLeadForm, trackCheckboxAdsLeadForm, trackContinueLeadForm, trackSuccessLeadForm } = useTracking();

  // Отслеживаем показ успешного сообщения
  useEffect(() => {
    if (isLeadSuccessfullySent) {
      trackSuccessLeadForm();
    }
  }, [isLeadSuccessfullySent, trackSuccessLeadForm]);

  // Обрабатываем ошибки из RTK Query
  useEffect(() => {
    if (isLeadError || isRefinLeadError) {
      apiToast.open({
        type: 'error',
        text: 'Что-то пошло не так, попробуйте позже',
      });
    }
  }, [isLeadError, apiToast, isRefinLeadError]);

  const onPhoneChange = (value: string) => {
    setLead({ ...lead, phone: value });
    if (value.length > 0) {
      trackInputLeadForm(value);
    }
    if (isPhoneError(error)) {
      const phoneError = validatePhone(value, false);
      if (!phoneError) {
        setError(null);
      }
    }
  };

  const onMortgageAmountRemainingChange = (value: number) => {
    setLead({ ...lead, mortgageAmountRemaining: value });

    // Очищаем ошибку при изменении
    if (error?.type === ValidationErrorType.MortgageAmount) {
      setError(null);
    }
  };

  const onMortgageRateChange = (value: number) => {
    setLead({ ...lead, mortgageRate: value });

    // Очищаем ошибку при изменении
    if (error?.type === ValidationErrorType.MortgageRate) {
      setError(null);
    }
  };

  const onAgreeToGetAdsChange = (_: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
    setLead({ ...lead, agreeToGetAds: value });
    trackCheckboxAdsLeadForm(value ? 'On' : 'Off');
    if (isAgreementError(error)) {
      setError(null);
    }
  };

  const onHasNoMortgageClick = () => {
    setLead({ ...lead, hasMortgage: false });
    sendRefinLead({ ...lead, hasMortgage: false });
  };

  const handleMortgageFormSubmit = () => {
    const amountError = validateMortgageAmount(lead.mortgageAmountRemaining);
    const rateError = validateMortgageRate(lead.mortgageRate);

    if (rateError) {
      setError(createValidationError(rateError, ValidationErrorType.MortgageRate));

      return;
    }

    if (amountError) {
      setError(createValidationError(amountError, ValidationErrorType.MortgageAmount));

      return;
    }

    setError(null);
    sendRefinLead(lead);
  };

  const handlePhoneFormSubmit = () => {
    trackContinueLeadForm();

    const phoneError = validatePhone(lead.phone, true);
    if (phoneError) {
      setError(createValidationError(phoneError, ValidationErrorType.Phone));

      return;
    }

    const agreementError = validateAgreement(lead.agreeToGetAds);
    if (agreementError) {
      setError(createValidationError(agreementError, ValidationErrorType.Agreement));

      return;
    }

    sendLead(lead.phone);
    setError(null);
  };

  return {
    error,
    lead,
    isLeadSending,
    isLeadSuccessfullySent,
    isRefinLeadSending,
    isRefinLeadSuccessfullySent,
    onPhoneChange,
    onMortgageAmountRemainingChange,
    onMortgageRateChange,
    onAgreeToGetAdsChange,
    onHasNoMortgageClick,
    handleMortgageFormSubmit,
    handlePhoneFormSubmit,
  };
};
